import { Analytics } from '@cobliteam/analytics';
import type { TrackingDevice } from '@cobliteam/api-lib';
import { ControlPositionType } from '@cobliteam/common-types';
import { useMap } from '@cobliteam/hooks';
import { changeMapRef } from '@cobliteam/store';
import { silenceFullscreenError } from '@cobliteam/ui-business/utils/silenceFullscreenError';
import { memo, useCallback, useEffect } from 'react';
import type { FullScreenHandle } from 'react-full-screen';
import Control from 'react-leaflet-custom-control';
import { useDispatch } from 'react-redux';

import { MapLegend } from '../../MapLegend/MapLegend';
import { FullscreenControl } from '../FullscreenControl/FullscreenControl';
import MapControlDropdown from '../MapControlDropdown/MapControlDropdown';
import MapStyleControl from '../MapStyleControl/MapStyleControl';
import { type MapType, MapTypeControl } from '../MapTypeControl/MapTypeControl';
import StreetView from '../StreetView/StreetView';
import ZoomControl from '../ZoomControl/ZoomControl';

export interface TrackingMapControlsProps {
  fullscreenHandler: FullScreenHandle;
  showGeofences: boolean;
  showMediaEvents: Boolean | undefined;
  showRoadSpeedEvents: boolean;
  showVehicleSpeedEvents: boolean;
  showGeofencesEvents: boolean;
  selectedDevice: TrackingDevice | undefined;
  mapType: MapType;
  useMapMatch: boolean;
  setMapType: () => {};
  setShowRoadSpeedEvents: () => {};
  setShowVehicleSpeedEvents: () => {};
  setShowGeofencesEvents: () => {};
  handleToggleGeofences: () => {};
  setMediaEvents: () => {};
  setUseMapMatch: () => {};
  showGeofenceForceVisibility: boolean;
  setShowGeofenceForceVisibility: () => {};
}

const TrackingMapControls = ({
  fullscreenHandler,
  showGeofences,
  showMediaEvents,
  showRoadSpeedEvents,
  showVehicleSpeedEvents,
  showGeofencesEvents,
  selectedDevice,
  mapType,
  useMapMatch,
  showGeofenceForceVisibility,
  setMapType,
  setShowRoadSpeedEvents,
  setShowVehicleSpeedEvents,
  setShowGeofencesEvents,
  handleToggleGeofences,
  setMediaEvents,
  setUseMapMatch,
  setShowGeofenceForceVisibility,
}: TrackingMapControlsProps) => {
  const { map } = useMap();
  const dispatch = useDispatch();

  const handleFullscreen = (active: boolean) => {
    Analytics.track('Tracking Fullscreen Toggled', { active });
    return active ? fullscreenHandler.enter().catch(silenceFullscreenError) : fullscreenHandler.exit().catch(silenceFullscreenError);
  };

  const createMapRef = useCallback(() => {
    if (map) {
      dispatch(changeMapRef(map));

      // remove tabindex of map and fix select option in search box
      const documentElement = document.getElementById('leaflet-map-tracking');
      if (documentElement) {
        documentElement.removeAttribute('tabindex');
      }
    }
  }, [map]);

  useEffect(() => {
    createMapRef();
    return () => {
      dispatch(changeMapRef(null));
    };
  }, [createMapRef, dispatch]);

  return (
    <>
      <Control position={ControlPositionType.TOP_LEFT}>
        <ZoomControl />
        <FullscreenControl active={fullscreenHandler.active} onToggle={handleFullscreen} />
      </Control>

      <Control position={ControlPositionType.TOP_RIGHT}>
        <div className='flex gap-2'>
          <MapStyleControl />
          <MapControlDropdown
            showGeofences={showGeofences}
            showRoadSpeedEvents={showRoadSpeedEvents}
            showVehicleSpeedEvents={showVehicleSpeedEvents}
            showMediaEvents={Boolean(showMediaEvents)}
            showGeofencesEvents={showGeofencesEvents}
            setShowRoadSpeedEvents={setShowRoadSpeedEvents}
            setShowVehicleSpeedEvents={setShowVehicleSpeedEvents}
            setShowGeofencesEvents={setShowGeofencesEvents}
            handleToggleGeofences={handleToggleGeofences}
            setMediaEvents={setMediaEvents}
            selectedDevice={selectedDevice}
            useMapMatch={useMapMatch}
            setUseMapMatch={setUseMapMatch}
          />
          <MapLegend showMediaEvents={Boolean(showMediaEvents)} />
        </div>
      </Control>

      <Control position={ControlPositionType.BOTTOM_LEFT}>
        <MapTypeControl currentType={mapType} onTypeChange={setMapType} />
      </Control>
      <StreetView />
    </>
  );
};

export default memo(TrackingMapControls);
